// src/lib/resources/types/glossary/terms.ts

// =============================================================================
// Glossary Terms Type Definitions
// =============================================================================

/**
 * @fileoverview Type definitions for glossary terms and related interfaces.
 * This module defines the core type system for the glossary feature, including:
 * - Base interfaces for terms, examples, and resources
 * - Specialized term types (Platform, Creative, Chart, etc.)
 * - Supporting types for calculations, benchmarks, and configuration
 * - Type guards and utility types
 * 
 * The type hierarchy is designed to be extensible while enforcing consistency
 * across different term categories. Each term type extends BaseGlossaryTerm
 * with its own specialized fields and validation requirements.
 * 
 * @module resources/types/glossary/terms
 * @since 1.0.0
 */

import type { ReactNode } from 'react'
import type { BaseGlossaryTerm } from '~/lib/resources/types/shared/base'
import type { 
  ChartType, 
  ValueFormatterType,
  OperationType,
  GlossaryCategory
} from '~/lib/resources/types/shared/enums'
import type { 
  ContentSectionTypes
} from '~/lib/resources/types/glossary/field-types'

// =============================================================================
// Core Resource Types
// =============================================================================

/**
 * Extension interface that adds structured field content support to term types.
 * This interface allows terms to leverage the flexible field content system
 * while maintaining backward compatibility with direct field properties.
 * 
 * Field content provides a type-safe way to represent different content patterns
 * (string lists, name-description pairs, etc.) consistently across the system.
 * 
 * @interface WithFieldContent
 * @property {Record<string, ContentSectionTypes>} [fieldContent] - Mapped field content for dynamic rendering
 * @property {string[]} [advantages] - Array of advantages (for backward compatibility)
 * @property {string[]} [limitations] - Array of limitations (for backward compatibility)
 * 
 * @example
 * ```typescript
 * // A term with both direct fields and flexible fieldContent
 * const term: ChartTerm = {
 *   // Direct field access (backward compatibility)
 *   limitations: ["Limited to time-series data"],
 *   
 *   // Structured field content (new approach)
 *   fieldContent: {
 *     advantages: {
 *       type: ContentType.STRING_LIST,
 *       sectionName: "advantages",
 *       items: ["Clear visual hierarchy", "Works well with sparse datasets"],
 *       display: {
 *         component: "bullet-list",
 *         layout: { compact: true }
 *       }
 *     },
 *     components: {
 *       type: ContentType.KEY_VALUE_LIST,
 *       sectionName: "components",
 *       items: [
 *         { name: "X-Axis", description: "Time periods" },
 *         { name: "Y-Axis", description: "Metric values" }
 *       ],
 *       keyInfo: {
 *         primaryKey: "name",
 *         secondaryKey: "description",
 *         allKeys: ["name", "description"]
 *       }, 
 *       display: {
 *         component: "definition-list",
 *         layout: { columns: 1 }
 *       }
 *     }
 *   }
 * }
 * ```
 */
export interface WithFieldContent {
  /** Flexible field content for dynamic rendering. Maps field names to their structured content representations. */
  fieldContent?: Record<string, ContentSectionTypes>
  /** Array of advantages for this term. For backward compatibility with direct field access. */
  advantages?: string[]
  /** Array of limitations for this term. For backward compatibility with direct field access. */
  limitations?: string[]
}

/**
 * External resource link with metadata
 * @interface ExternalResource
 * @property {string} title - Display title of the external resource
 * @property {string} url - Full URL to the external resource
 * @property {string} [description] - Optional description of the resource content
 * @property {'article' | 'video' | 'tool' | 'documentation'} [type] - Resource type classification
 * @property {boolean} [isInternal] - Flag indicating if this is an internal AdSights resource
 * @property {string} [resourceId] - Optional internal resource identifier
 * @property {Record<string, string>} [resourceParams] - Parameters extracted from internal URL paths
 * 
 * @deprecated Use SupplementalResourceReference from shared/base.ts instead
 */
export interface ExternalResource {
  /** Display title of the external resource */
  title: string
  /** Full URL to the external resource */
  url: string
  /** Optional description of the resource content */
  description?: string
  /** Resource type classification */
  type?: 'article' | 'video' | 'tool' | 'documentation'
  /** Flag indicating if this is an internal AdSights resource */
  isInternal?: boolean
  /** Optional internal resource identifier */
  resourceId?: string
  /** Parameters extracted from internal URL paths */
  resourceParams?: Record<string, string>
}

/**
 * Related term with relationship context
 * @interface RelatedTerm
 * @property {string} title - Title of the related term
 * @property {string} slug - URL-friendly identifier for the term
 * @property {'parent'|'child'|'similar'|'opposite'|'component'|'variant'|'alternative'} [relationship] - Relationship classification
 * @property {string} [description] - Optional description explaining the relationship to the main term
 */
export interface RelatedTerm {
  /** Title of the related term */
  title: string
  /** URL-friendly identifier for the term */
  slug: string
  /** Optional description explaining the relationship */
  description?: string
  /** Type of relationship to the main term */
  relationship?: 'parent' | 'child' | 'similar' | 'opposite' | 'component' | 'variant' | 'alternative'
}

// =============================================================================
// Example & Visualization Interfaces
// =============================================================================

/**
 * Example with additional contextual information
 * Used to provide examples with explanatory context for better understanding
 * @interface ExampleWithContext
 * @property {string} text - The actual example text or content
 * @property {string} context - Additional explanation or context about the example
 * @property {string} [platform] - The platform this example is for
 */
export interface ExampleWithContext {
  /** The actual example text or content */
  text: string
  /** Additional explanation or context about the example */
  context: string
  /** The platform this example is for */
  platform?: string
}

/**
 * Chart example with visualization
 * @interface ChartExample
 * @property {Record<string, unknown>[]} data - Data used to generate the chart
 * @property {ChartConfig} config - Configuration for the chart
 * @property {string} description - Description of the chart and its purpose
 * @property {ReactNode} [visualization] - Visual representation of the chart
 * @property {string} [context] - Additional context or explanation about the chart
 * @property {string} [platform] - The platform this chart is for
 * 
 * @see {@link ChartConfig} - For chart configuration details
 */
export interface ChartExample {
  /** Data used to generate the chart */
  data: Record<string, unknown>[]
  /** Configuration for the chart */
  config: ChartConfig
  /** Description of the chart and its purpose */
  description: string
  /** Visual representation of the chart */
  visualization?: ReactNode
  /** Additional context or explanation about the chart */
  context?: string
  /** The platform this chart is for */
  platform?: string
}

/**
 * Union type for all possible example formats
 * Can be a simple string, array of strings, an example with context, or a chart example
 * @typedef {(string|string[]|ExampleWithContext|ChartExample)} Example
 */
export type Example = string | string[] | ExampleWithContext | ChartExample

// =============================================================================
// Specific Term Types
// =============================================================================

/**
 * Platform-specific glossary term
 * Represents terms that are specific to particular platforms or tools
 * @interface PlatformTerm
 * @extends {BaseGlossaryTerm}
 * @extends {WithFieldContent}
 * @property {Extract<GlossaryCategory, 'platform'>} category - Category discriminator
 * @property {string[]} [platformName] - Platform names this term is specific to
 * @property {string[]} [platformSpecificFeatures] - Features specific to a single platform
 * @property {Array<{platform: string, feature: string, comparison: string}>} [alternatives] - Alternatives to this feature
 */
export interface PlatformTerm extends BaseGlossaryTerm, WithFieldContent {
  /** Category discriminator */
  category: Extract<GlossaryCategory, 'platform'>
  /** Platform names this term is specific to */
  platformName?: string[]
  /** Features specific to a single platform */
  platformSpecificFeatures?: string[]
  /** Alternatives to this feature */
  alternatives?: Array<{
    platform: string
    feature: string
    comparison: string
  }>
}

/**
 * Creative-specific glossary term
 * @interface CreativeTerm
 * @extends {BaseGlossaryTerm}
 * @extends {WithFieldContent}
 * @property {Extract<GlossaryCategory, 'creative'>} category - Category discriminator
 * @property {Array<{imageUrl: string, caption: string, description?: string}>} [visualExamples] - Visual examples of the creative term
 * @property {string[]} [applicablePlatforms] - Applicable platforms for creative content
 */
/**
 * Mixin interface carrying the four "enrichment" fields originally
 * exclusive to `MetricTerm`. The Q2 2026 SEO audit Tier-1 pass extended
 * these to a handful of creative + general terms where the underlying
 * signal genuinely applies (Creative Fatigue → CTR-decay formula,
 * Incrementality → lift % formula + per-channel benchmarks). Pulled
 * into one named interface so:
 *
 *   1. The three identical optional-field declarations on
 *      `MetricTerm` / `CreativeTerm` / `GeneralTerm` stay in sync —
 *      a fifth field can't drift between them.
 *   2. The build-script + export-md cast (`as typeof term & { ... }`)
 *      can be replaced with a single `(term as EnrichableTerm)`
 *      narrowing — no field-by-field intersection literal at the use
 *      site. `r3314734076`.
 *   3. The CalculationSection layout gates on `'calculation' in term`
 *      against the union of all enrichable shapes rather than
 *      `isMetricTerm`-only.
 *
 * Every field is optional so the mixin is structurally a no-op for
 * terms that don't carry enrichment data — existing creative/general
 * YAML stays valid without changes.
 */
export interface EnrichableTerm {
  /** Metric-style calculation block (formula + variables) */
  calculation?: MetricCalculation
  /** Industry-segment benchmark ranges rendered above the calculation tab */
  segmentBenchmarks?: SegmentBenchmark[]
  /** Compact formula callout for rich-snippet eligibility */
  formulaCallout?: FormulaCallout
  /** Plain-language paragraph describing how AdSights helps track / improve this enrichable term */
  howAdSightsHelps?: string
  /**
   * Framing variant for the `howAdSightsHelps` callout. Absent (default)
   * renders the analytics framing — "How AdSights helps you track X" with
   * the `/request-access` CTA. `'framework'` renders the storefront-funnel
   * framing — "How AdSights helps with X" with an Ads Framework CTA to
   * `/frameworks/ads-framework` — for terms whose copy describes the
   * production pipeline rather than metric tracking (`r3566303320`). The
   * md exporter mirrors the same split so `.md` twins can't drift from
   * the HTML.
   */
  howAdSightsHelpsVariant?: 'framework'
}

export interface CreativeTerm extends BaseGlossaryTerm, WithFieldContent, EnrichableTerm {
  /** Category discriminator */
  category: Extract<GlossaryCategory, 'creative'>
  /** Visual examples of the creative term */
  visualExamples?: Array<{
    imageUrl: string
    caption: string
    description?: string
  }>
  /** Applicable platforms for creative content */
  applicablePlatforms?: string[]
}

/**
 * Data requirements for a chart
 * @interface DataRequirements
 * @property {string} format - The format of the data (e.g. 'time-series', 'categorical')
 * @property {string} structure - The structure of the data (e.g. 'standard', 'nested')
 * @property {number} [minimumPoints] - Minimum number of data points required
 */
export interface DataRequirements {
  /** The format of the data */
  format: string
  /** The structure of the data */
  structure: string
  /** Minimum number of data points required */
  minimumPoints?: number
}

/**
 * Chart-specific glossary term
 * @interface ChartTerm
 * @extends {BaseGlossaryTerm}
 * @extends {WithFieldContent}
 * @property {Extract<GlossaryCategory, 'charts'>} category - Category discriminator
 * @property {ChartConfig} chartConfig - Configuration for the chart rendering
 * @property {ChartConfig} chartConfig.type - Chart type
 * @property {ChartConfig} chartConfig.categories - Data categories to display
 * @property {ChartConfig} chartConfig.index - Index field for data
 * @property {ChartConfig} chartConfig.colors - Chart colors
 * @property {ChartConfig} chartConfig.valueFormatter - Value formatter type
 * @property {ChartConfig} chartConfig.options - Additional chart-specific options
 * @property {ChartConfig} chartConfig.options.stacked - Enable stacked visualization
 * @property {ChartConfig} chartConfig.options.showGrid - Show grid lines
 * @property {ChartConfig} chartConfig.options.showLegend - Display chart legend
 * @property {ChartConfig} chartConfig.options.showTooltip - Enable tooltips
 * @property {ChartConfig} chartConfig.options.aspectRatio - Chart aspect ratio
 * @property {ChartConfig} chartConfig.options.minValue - Minimum value bound
 * @property {ChartConfig} chartConfig.options.maxValue - Maximum value bound
 * @property {ChartConfig} chartConfig.options.stepSize - Value axis step size
 * @property {DataRequirements} dataRequirements - Data requirements for the chart
 * @property {string} dataRequirements.format - The format of the data (e.g. 'time-series', 'categorical')
 * @property {string} dataRequirements.structure - The structure of the data (e.g. 'standard', 'nested')
 * @property {number} [dataRequirements.minimumPoints] - Minimum number of data points required
 * @property {string[]} [bestUsedFor] - Recommended use cases
 * @property {string[]} [limitations] - Limitations of the chart type
 * @property {string[]} [alternatives] - Alternative chart types
 * @property {ChartExample[]} [examples] - Chart examples
 * 
 * @see {@link ChartConfig} - For chart configuration details
 * @see {@link ChartExample} - For chart example structure
 */
export interface ChartTerm extends BaseGlossaryTerm, WithFieldContent {
  /** Category discriminator */
  category: Extract<GlossaryCategory, 'charts'>
  /** Chart rendering configuration */
  chartConfig: ChartConfig
  /** Recommended use cases */
  bestUsedFor: string[]
  /** Known limitations */
  limitations: string[]
  /** Chart examples */
  examples?: ChartExample[]
  /** Alternative chart types */
  alternatives?: string[]
  /** Data format requirements */
  /** Data requirements for the chart */
  dataRequirements: DataRequirements
  /**
   * Marketing-analytics use cases for this chart type, rendered as a
   * "When to use this chart" section. Deliberately distinct from
   * `SegmentBenchmark` (which charts must NOT carry): a chart has no numeric
   * benchmark range, so routing chart use-cases through `BenchmarksTable`
   * ("Industry Benchmarks → Typical Range") is semantically wrong. Each row
   * pairs a marketing use case with the data shape it expects, plus a source
   * citation and an optional caveat. Reviewer thread r3478636503.
   */
  chartUseCases?: ChartUseCase[]
}

/**
 * A single marketing-analytics use case for a chart type. Maps to the
 * "When to use this chart" section on chart glossary pages.
 *
 * @interface ChartUseCase
 */
export interface ChartUseCase {
  /** The marketing use case, e.g. "Weekly KPI trends" */
  useCase: string
  /** The data shape this use case expects, e.g. "MER, blended CAC over 8–12 weeks" */
  dataShape: string
  /** Source citation supporting the recommendation */
  source: string
  /** Optional one-line caveat or guidance note */
  note?: string
}

/**
 * Metric-specific term
 * @interface MetricTerm
 * @extends {BaseGlossaryTerm} - Base glossary term properties
 * @inheritdoc BaseGlossaryTerm
 * @extends {WithFieldContent} - Field content properties
 * @inheritdoc WithFieldContent
 * @property {Extract<GlossaryCategory, 'metrics'>} category - Category discriminator
 * @property {Example[]} [examples] - Usage examples with context
 * @property {MetricCalculation} calculation - Calculation configuration
 * @property {{byPlatform?: MetricBenchmark[]}} [benchmarks] - Benchmarks by platform
 * @property {{historicalData: Record<string, number>[], comparisonData?: Record<string, number>[], valueFormatterType: ValueFormatterType}} [components] - Component data
 * 
 * @see {@link MetricCalculation} - For metric calculation details
 * @see {@link MetricBenchmark} - For benchmark structure
 * @see {@link BaseGlossaryTerm} - For base glossary term properties
 * @see {@link WithFieldContent} - For field content properties
 */
export interface MetricTerm extends BaseGlossaryTerm, WithFieldContent, EnrichableTerm {
  /** Category discriminator */
  category: Extract<GlossaryCategory, 'metrics'>
  /** Usage examples with context */
  examples?: Example[]
  /**
   * Calculation configuration — REQUIRED on metric terms (the
   * canonical case `EnrichableTerm` was extracted from), so the
   * inherited optional `calculation?: MetricCalculation` is narrowed
   * here to a required field. TypeScript's interface-merging accepts
   * the narrower required type as a valid override of the optional
   * base, so existing `term.calculation.formula` callers stay
   * non-null without runtime branching.
   */
  calculation: MetricCalculation
  /** Benchmarks by platform */
  benchmarks?: {
    byPlatform?: MetricBenchmark[]
  }
  /** Component data */
  components?: {
    /** Historical trend data */
    historicalData: Record<string, number>[]
    /** Comparative analysis data */
    comparisonData?: Record<string, number>[]
    /** Value formatting type */
    valueFormatterType: ValueFormatterType
  }
}

/**
 * General glossary term
 * @interface GeneralTerm
 * @extends {BaseGlossaryTerm}
 * @extends {WithFieldContent}
 * @property {Extract<GlossaryCategory, 'general'>} category - Category discriminator
 * @property {Example[]} [examples] - Contextual examples (string | string[] | ChartExample | ExampleWithContext)
 * 
 * @see {@link BaseGlossaryTerm} - For base glossary term properties
 * @see {@link WithFieldContent} - For field content properties
 */
export interface GeneralTerm extends BaseGlossaryTerm, WithFieldContent, EnrichableTerm {
  /** Category discriminator */
  category: Extract<GlossaryCategory, 'general'>
  /** Contextual examples */
  examples?: Example[]
}

/**
 * Union type of all glossary term types
 * Uses discriminated union pattern with 'category' as discriminator
 * @typedef {(PlatformTerm|CreativeTerm|ChartTerm|MetricTerm|GeneralTerm)} GlossaryTerm
 */
export type GlossaryTerm = 
  | PlatformTerm 
  | CreativeTerm 
  | ChartTerm 
  | MetricTerm 
  | GeneralTerm

// =============================================================================
// Configuration & Utility Interfaces
// =============================================================================

/**
 * Configuration for chart examples
 * @interface ChartConfig
 * @property {ChartType} type - Chart type
 * @property {string[]} [categories] - Data categories to display (used by Bar, Line, Area, Scatter - optional for others)
 * @property {string} index - Index field for data (X-axis for Bar/Line/Area, Name key for Pie)
 * @property {string} [valueAccessor] - Key for the object containing category values, if data is nested (e.g., 'revenue' if data is { year: '2023', revenue: { product: 100 } })
 * @property {string} [value] - Value field for data (used by Pie/Donut)
 * @property {'horizontal' | 'vertical'} [layout] - Explicit layout orientation (defaults to vertical)
 * @property {'sequential' | 'categorical' | 'diverging'} [colorPalette] - Type of color palette to use (defaults to categorical)
 * @property {number} [areaOpacity] - Opacity level (0-1) for Area chart fills (defaults to 0.4)
 * @property {ValueFormatterType} [valueFormatter] - Value formatter type
 * @property {Object} [options] - Additional chart-specific options
 * @property {boolean} [options.stacked] - Enable stacked visualization (Bar/Area)
 * @property {boolean} [options.showGrid] - Show grid lines
 * @property {boolean} [options.showLegend] - Display chart legend
 * @property {boolean} [options.showTooltip] - Enable tooltips
 * @property {number} [options.aspectRatio] - Chart aspect ratio
 * @property {number} [options.minValue] - Minimum value bound (Y-axis)
 * @property {number} [options.maxValue] - Maximum value bound (Y-axis)
 * @property {number} [options.stepSize] - Value axis step size (Y-axis)
 * @property {boolean} [options.interpolateMissingValues] - Interpolate missing values (Line/Area)
 * @property {number | string} [options.innerRadius] - Inner radius for Donut chart (pixel value or percentage string like '60%')
 * @property {number | string} [options.outerRadius] - Outer radius for Pie/Donut chart (pixel value or percentage string like '80%')
 * @property {string} [options.xAxisKey] - Key for the X-axis data (Scatter plot). Defaults to 'index' if not provided.
 * @property {string} [options.yAxisKey] - Key for the Y-axis data (Scatter plot). Defaults to categories[0] if not provided.
 * @property {string} [options.sizeKey] - Explicit data key for bubble size (Bubble chart). Defaults to categories[0] or other heuristic if not set.
 * @since 1.0.0
 */
export interface ChartConfig {
  /** Chart type */
  type: ChartType
  /** Data categories to display (used by Bar, Line, Area, Radar - optional for others) */
  categories?: string[]
  /** Index field for data (X-axis for Bar/Line/Area, Name key for Pie) */
  index: string
  /** Key for the object containing category values, if data is nested (e.g., 'revenue' if data is { year: '2023', revenue: { product: 100 } }) */
  valueAccessor?: string
  /** Value field for data (used by Pie/Donut) */
  value?: string
  /** Explicit layout orientation (defaults to vertical) */
  layout?: 'horizontal' | 'vertical'
  /** Type of color palette to use (defaults to categorical) */
  colorPalette?: 'sequential' | 'categorical' | 'diverging'
  /** Opacity level (0-1) for Area chart fills (defaults to 0.4) */
  areaOpacity?: number
  /** Value formatter type */
  valueFormatter?: ValueFormatterType
  /** Additional chart-specific options */
  options?: {
    /** Enable stacked visualization (Bar/Area) */
    stacked?: boolean
    /** Show grid lines */
    showGrid?: boolean
    /** Display chart legend */
    showLegend?: boolean
    /** Enable tooltips */
    showTooltip?: boolean
    /** Chart aspect ratio */
    aspectRatio?: number
    /** Minimum value bound (Y-axis) */
    minValue?: number
    /** Maximum value bound (Y-axis) */
    maxValue?: number
    /** Value axis step size (Y-axis) */
    stepSize?: number
    /** Interpolate missing values (Line/Area) */
    interpolateMissingValues?: boolean
    /** Inner radius for Donut chart (pixel value or percentage string like '60%') */
    innerRadius?: number | string
    /** Outer radius for Pie/Donut chart (pixel value or percentage string like '80%') */
    outerRadius?: number | string
    /** Key for the X-axis data (Scatter/Bubble). Defaults to 'index' if not provided. */
    xAxisKey?: string
    /** Key for the Y-axis data (Scatter/Bubble). Defaults to categories[0] if not provided. */
    yAxisKey?: string
    /** Key for bubble size (Bubble). Defaults to categories[0] or other heuristic if not provided. */
    sizeKey?: string
  }
}

// =============================================================================
// Metric Analysis Types
// =============================================================================

/**
 * Metric calculation configuration
 * @interface MetricCalculation
 * @property {MetricComponent[]} [components] - Components used in calculation
 * @property {string} [formula] - Formula representation
 * @property {OperationType} [operation] - Type of operation
 * @property {'%' | '$' | 'x' | 'ratio' | ''} [resultUnit] - Unit for result
 * @property {string} [explanation] - Explanation of the formula
 * @property {Array<{name: string, description: string}>} [variables] - Variables used in the formula
 */
export interface MetricCalculation {
  /** Components used in calculation */
  components?: MetricComponent[]
  /** Formula representation */
  formula?: string
  /** Type of operation */
  operation?: OperationType
  /** Unit for result */
  resultUnit?: '%' | '$' | 'x' | 'ratio' | ''
  /** Explanation of the formula */
  explanation?: string
  /** Variables used in the formula */
  variables?: Array<{name: string; description: string}>
}

/**
 * Metric component definition
 * Component used in metric calculations
 * @interface MetricComponent
 * @property {string} name - Display name of the component
 * @property {string} description - Description of what this component represents
 * @property {string} dataKey - Key to access component data
 * @property {number | string} [defaultValue] - Default value for this component
 */
export interface MetricComponent {
  /** Display name of the component */
  name: string
  /** Description of what this component represents */
  description: string
  /** Key to access component data */
  dataKey: string
  /** Default value for this component */
  defaultValue?: number | string
}

/**
 * Metric benchmark data
 * Benchmark data for metrics across different platforms
 * Used to compare and analyze performance metrics across various platforms
 * @interface MetricBenchmark
 * @property {string} platform - Platform name (e.g. "Facebook", "TikTok", "YouTube")
 * @property {string} category - The category of the metric
 * @property {string | number} poor - Poor performance threshold
 * @property {string | number} average - Average performance value
 * @property {string | number} good - Good performance threshold
 * @property {string} [note] - Optional additional context or caveats
 * @example
 * // Example benchmark for CTR on Facebook
 * const facebookCtrBenchmark: MetricBenchmark = {
 *   platform: "Facebook",
 *   category: "engagement",
 *   poor: "0.5%",
 *   average: "1.0%",
 *   good: "2.0%",
 *   note: "Values vary by industry and ad placement"
 * }
 */
export interface MetricBenchmark {
  /** The platform this benchmark is for */
  platform: string
  /** The category of the metric this benchmark is for */
  category: string
  /** The poor value of the metric for this platform */
  poor: string | number
  /** The average value of the metric for this platform */
  average: string | number
  /** The good value of the metric for this platform */
  good: string | number
  /** Optional additional context or caveats about the benchmark */
  note?: string
}

/**
 * Industry-segment benchmark for a metric, used to render Databox-style ranges
 * on metric glossary pages. Independent of platform; segments are typically
 * verticals like "B2B SaaS", "E-commerce", "Lead Gen".
 *
 * @interface SegmentBenchmark
 */
export interface SegmentBenchmark {
  /** Industry vertical / use case, e.g. "B2B SaaS" */
  segment: string
  /** Typical range, e.g. "3.0x – 5.0x" or "1% – 3%" */
  range: string
  /** Optional median value within the range */
  median?: string
  /**
   * Source citations for the data, normalized to an array at build time.
   * Authors write either `source: "WordStream 2024"`, a `;`-concatenated
   * string `source: "WordStream 2024; Triple Whale 2024"`, or a YAML list
   * `source: ["WordStream 2024", "Triple Whale 2024"]` in YAML.
   * process-glossary.ts coerces all three into `sources: string[]` on the
   * compiled output — renderers consume only this canonical form.
   */
  sources?: string[]
  /** Optional one-line note explaining what drives the range in this segment */
  note?: string
}

/**
 * Compact, scannable callout for a metric's formula, rendered above the
 * full Calculation tab. Used by metric glossary pages to win featured snippets.
 *
 * @interface FormulaCallout
 */
/**
 * Allowed result-unit indicators for FormulaCallout.resultUnit.
 * Declared as a runtime tuple so both the literal type AND the runtime
 * validator (process-glossary.ts) derive from a single source.
 */
export const FORMULA_RESULT_UNITS = ['', '%', '$', 'x', 'ratio'] as const
export type FormulaResultUnit = typeof FORMULA_RESULT_UNITS[number]

export interface FormulaCallout {
  /** The formula expression, e.g. "Revenue / Ad Spend" */
  expression: string
  /** Result unit indicator (same vocabulary as MetricCalculation.resultUnit) */
  resultUnit?: FormulaResultUnit
  /** Optional plain-English restatement for users who prefer prose */
  plainEnglish?: string
}

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Validates a MetricCalculation object
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a valid MetricCalculation
 */
function isValidMetricCalculation(value: unknown): value is MetricCalculation {
  return (
    typeof value === 'object' &&
    value !== null &&
    (
      !('formula' in value) ||
      typeof value.formula === 'string'
    ) &&
    (
      !('components' in value) ||
      Array.isArray(value.components)
    ) &&
    (
      !('operation' in value) ||
      typeof value.operation === 'string'
    )
  )
}

/**
 * Validates a ChartConfig object
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a valid ChartConfig
 */
function isValidChartConfig(value: unknown): value is ChartConfig {
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'index' in value &&
    typeof value.index === 'string'
  )
}

/**
 * Type guard to check if a value is a valid GlossaryTerm
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a valid GlossaryTerm
 */
export function isGlossaryTerm(value: unknown): value is GlossaryTerm {
  if (
    typeof value !== 'object' ||
    value === null ||
    !('resourceType' in value) ||
    value.resourceType !== 'glossary-term' ||
    !('category' in value) ||
    typeof value.category !== 'string' ||
    !['platform', 'creative', 'charts', 'metrics', 'general'].includes(value.category) ||
    !('shortDescription' in value) ||
    typeof value.shortDescription !== 'string' ||
    !('definition' in value) ||
    typeof value.definition !== 'string' ||
    !('id' in value) ||
    typeof value.id !== 'string' ||
    !('title' in value) ||
    typeof value.title !== 'string' ||
    !('slug' in value) ||
    typeof value.slug !== 'string'
  ) {
    return false
  }

  // Category-specific validation
  switch (value.category) {
    case 'platform':
      return (
        (!('platformName' in value) || Array.isArray(value.platformName)) &&
        (!('examples' in value) || Array.isArray(value.examples))
      )
    case 'creative':
      return (
        (!('visualExamples' in value) || Array.isArray(value.visualExamples)) &&
        (!('applicablePlatforms' in value) || Array.isArray(value.applicablePlatforms))
      )
    case 'charts':
      return (
        'chartConfig' in value &&
        isValidChartConfig(value.chartConfig) &&
        'dataRequirements' in value &&
        typeof value.dataRequirements === 'object' &&
        value.dataRequirements !== null
      )
    case 'metrics':
      return (
        'calculation' in value &&
        isValidMetricCalculation(value.calculation)
      )
    case 'general':
      return (!('examples' in value) || Array.isArray(value.examples))
    default:
      return false
  }
}

/**
 * Type guard to check if a value is a PlatformTerm
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a PlatformTerm
 * @see {@link PlatformTerm} - For platform term structure
 */
export function isPlatformTerm(value: unknown): value is PlatformTerm {
  return isGlossaryTerm(value) && value.category === 'platform'
}

/**
 * Type guard to check if a value is a CreativeTerm
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a CreativeTerm
 * @see {@link CreativeTerm} - For creative term structure
 */
export function isCreativeTerm(value: unknown): value is CreativeTerm {
  return isGlossaryTerm(value) && value.category === 'creative'
}

/**
 * Type guard to check if a value is a ChartTerm
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a ChartTerm
 * @see {@link ChartTerm} - For chart term structure
 */
export function isChartTerm(value: unknown): value is ChartTerm {
  return isGlossaryTerm(value) && value.category === 'charts'
}

/**
 * Type guard to check if a value is a MetricTerm
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a MetricTerm
 * @see {@link MetricTerm} - For metric term structure
 */
export function isMetricTerm(value: unknown): value is MetricTerm {
  return isGlossaryTerm(value) && value.category === 'metrics'
}

/**
 * Type guard to check if a value is a GeneralTerm
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a GeneralTerm
 * @see {@link GeneralTerm} - For general term structure
 */
export function isGeneralTerm(value: unknown): value is GeneralTerm {
  return isGlossaryTerm(value) && value.category === 'general'
}

/**
 * Type guard to check if a value is an ExampleWithContext
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is an ExampleWithContext
 * @see {@link ExampleWithContext} - For example with context structure
 */
export function isExampleWithContext(value: unknown): value is ExampleWithContext {
  return (
    typeof value === 'object' &&
    value !== null &&
    'text' in value &&
    typeof value.text === 'string' &&
    'context' in value &&
    typeof value.context === 'string'
  )
}

/**
 * Type guard to check if a value is a ChartExample
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a ChartExample
 * @see {@link ChartExample} - For chart example structure
 */
export function isChartExample(value: unknown): value is ChartExample {
  return (
    typeof value === 'object' &&
    value !== null &&
    'data' in value &&
    Array.isArray(value.data) &&
    'config' in value &&
    isValidChartConfig(value.config) &&
    'description' in value &&
    typeof value.description === 'string'
  )
}
